import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Trophy, ArrowLeft, Zap, Swords } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScoreBar from "@/components/duel/ScoreBar";
import QuestionCard from "@/components/duel/QuestionCard";
import ResultReveal from "@/components/duel/ResultReveal";
import DuelChat from "@/components/duel/DuelChat";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export default function DuelRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  const [phase, setPhase] = useState("connecting");
  const [scenario, setScenario] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [opponentAnswered, setOpponentAnswered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [scores, setScores] = useState({});
  const [finalResults, setFinalResults] = useState(null);
  const [playerNames, setPlayerNames] = useState({});
  const [opponentId, setOpponentId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);

  // Keep a ref to the latest user so the room-scoped effects below never
  // capture a stale closure, while still only reconnecting when roomId changes.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    api.get(`/duel/room/${roomId}`).then(({ data }) => {
      setPlayerNames(data.player_names || {});
      setOpponentId((data.player_ids || []).find((id) => id !== userRef.current?.user_id));
    }).catch(() => {});
  }, [roomId]);

  useEffect(() => {
    let active = true;
    const ws = new WebSocket(`${WS_BASE}/api/duel/ws/${roomId}`);
    wsRef.current = ws;
    ws.onopen = () => { if (active) setPhase((p) => (p === "connecting" ? "waiting" : p)); };
    ws.onerror = () => { if (active) setPhase("error"); };
    ws.onmessage = (evt) => {
      if (!active) return;
      const msg = JSON.parse(evt.data);
      if (msg.type === "waiting_for_opponent") setPhase("waiting");
      else if (msg.type === "duel_start") {
        setScenario({ scenario: msg.scenario, difficulty: msg.difficulty });
        setPhase("starting");
      } else if (msg.type === "question") {
        setQuestion(msg); setSelectedIndex(null); setAnswered(false); setOpponentAnswered(false);
        setResult(null); setTimeLeft(msg.time_limit); setPhase("question");
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
      } else if (msg.type === "opponent_answered") {
        if (msg.user_id !== userRef.current?.user_id) setOpponentAnswered(true);
      } else if (msg.type === "question_result") {
        clearInterval(timerRef.current);
        setResult(msg); setScores(msg.scores); setPhase("result");
      } else if (msg.type === "duel_complete") {
        clearInterval(timerRef.current);
        setFinalResults(msg.results); setPhase("complete"); refreshUser();
      } else if (msg.type === "chat_message") {
        setChatMessages((prev) => [...prev, msg]);
      }
    };
    return () => {
      active = false;
      clearInterval(timerRef.current);
      ws.onerror = null;
      ws.onclose = null;
      ws.onmessage = null;
      ws.onopen = null;
      ws.close();
    };
  }, [roomId, refreshUser]);

  const submitAnswer = (index) => {
    if (answered || !wsRef.current) return;
    setSelectedIndex(index); setAnswered(true);
    wsRef.current.send(JSON.stringify({ type: "submit_answer", question_index: question.index, selected_index: index }));
  };

  const sendChatMessage = (text) => {
    if (!wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: "chat_message", text }));
  };

  const myScore = scores[user?.user_id]?.points ?? 0;
  const oppScore = opponentId ? scores[opponentId]?.points ?? 0 : 0;

  if (phase === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="duel-room-error">
        <p className="text-slate-300">Connection lost. This duel room may no longer be available.</p>
        <button onClick={() => navigate("/duel")} className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Duel Hub</button>
      </div>
    );
  }

  if (phase === "complete" && finalResults) {
    const won = !finalResults.is_draw && finalResults.winner_id === user?.user_id;
    const myChange = finalResults.rating_changes?.[user?.user_id] ?? 0;
    const myBadges = finalResults.newly_earned_achievements?.[user?.user_id] || [];
    const myFinal = finalResults.scores?.[user?.user_id];
    const accuracy = myFinal ? Math.round((100 * myFinal.correct) / 5) : 0;
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="duel-complete-screen">
        <Trophy className={`w-16 h-16 mx-auto mb-4 ${won ? "text-[#FBBF24]" : "text-slate-400"}`} />
        <h1 className="font-heading text-3xl font-semibold text-white" data-testid="duel-outcome-heading">
          {finalResults.is_draw ? "It's a Draw!" : won ? "Victory!" : "Defeat"}
        </h1>
        <div className="grid grid-cols-2 gap-4 mt-8">
          <div className="glass-panel rounded-xl p-4"><p className="text-2xl font-bold text-indigo-300" data-testid="duel-final-score">{myFinal?.points ?? 0}</p><p className="text-xs text-slate-400 mt-1">Final Score</p></div>
          <div className="glass-panel rounded-xl p-4"><p className="text-2xl font-bold text-white">{accuracy}%</p><p className="text-xs text-slate-400 mt-1">Accuracy</p></div>
          <div className="glass-panel rounded-xl p-4"><p className={`text-2xl font-bold ${myChange >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="duel-rating-change">{myChange >= 0 ? "+" : ""}{myChange}</p><p className="text-xs text-slate-400 mt-1">Rating Change</p></div>
          <div className="glass-panel rounded-xl p-4"><p className="text-2xl font-bold text-[#FBBF24] flex items-center justify-center gap-1"><Zap className="w-5 h-5" />{won ? 150 : finalResults.is_draw ? 75 : 40}</p><p className="text-xs text-slate-400 mt-1">XP Earned</p></div>
        </div>
        {myBadges.length > 0 && <p className="text-sm text-[#FBBF24] mt-6" data-testid="duel-badge-earned">🏆 New badge unlocked: {myBadges[0].replace(/_/g, " ")}</p>}
        <button onClick={() => navigate("/duel")} data-testid="duel-back-to-hub-btn" className="mt-8 text-sm font-semibold px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors">
          Back to Duel Hub
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="duel-room-page">
      <button onClick={() => navigate("/duel")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6" data-testid="duel-room-back-btn">
        <ArrowLeft className="w-4 h-4" /> Leave Duel
      </button>

      {(phase === "connecting" || phase === "waiting") && (
        <div className="glass-panel rounded-2xl p-12 text-center" data-testid="duel-waiting-panel">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400 mx-auto mb-4" />
          <p className="text-slate-300">Waiting for your opponent to connect...</p>
        </div>
      )}

      {phase === "starting" && scenario && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-panel rounded-2xl p-12 text-center" data-testid="duel-starting-panel">
          <Swords className="w-10 h-10 text-rose-400 mx-auto mb-4" />
          <p className="text-xs font-bold uppercase tracking-wider text-indigo-400 mb-1">{scenario.difficulty}</p>
          <h2 className="font-heading text-2xl font-semibold text-white">{scenario.scenario}</h2>
          <p className="text-sm text-slate-400 mt-2">Get ready — the duel is starting!</p>
        </motion.div>
      )}

      {(phase === "question" || phase === "result") && question && (
        <div className="space-y-4">
          <ScoreBar me={user?.name || "You"} opponent={playerNames[opponentId] || "Opponent"} myScore={myScore} oppScore={oppScore} />
          {phase === "question" && (
            <QuestionCard question={question} timeLeft={timeLeft} timeLimit={question.time_limit} selectedIndex={selectedIndex} answered={answered} opponentAnswered={opponentAnswered} onSelect={submitAnswer} />
          )}
          {phase === "result" && result && <ResultReveal result={result} question={question} myId={user?.user_id} />}
        </div>
      )}

      {phase !== "connecting" && <DuelChat messages={chatMessages} myUserId={user?.user_id} onSend={sendChatMessage} />}
    </div>
  );
}
